import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { Seller } from '@core/models/dealer.model';
import { BenefitProgram } from '@shared/enums/benefit-program-type.enum';
import { Sponsor, SponsorType } from '@shared/models/sponsor.model';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { select, Store } from '@ngxs/store';
import { SellerSearchAction } from '@core/states/dealer/dealer.actions';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';

@Component({
  selector: 'app-benefit-program-options',
  templateUrl: './benefit-program-options.component.html',
  styleUrls: ['./benefit-program-options.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatSelect,
    SpinnerComponent,
  ],
})
export class BenefitProgramOptionsComponent implements OnInit, OnDestroy {
  private store = inject(Store);

  BenefitProgram = BenefitProgram;
  SponsorType = SponsorType;

  @Input() sponsor: Sponsor;
  @Output() sponsorSelected = new EventEmitter<Seller>();
  @Output() sponsorTypeSelected = new EventEmitter<SponsorType>();

  searchResult = select(DealerStateSelectors.searchResult);
  isCurrentlySearching = select(DealerStateSelectors.isCurrentlySearching);

  form: FormGroup;

  subscriptions$: Subscription[] = [];

  ngOnInit() {
    this.form = this.buildForm(this.sponsor);

    this.subscriptions$.push(
      this.form.controls.searchValue.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(updateValue => this.store.dispatch(new SellerSearchAction(updateValue)))
        )
        .subscribe()
    );
  }

  onSponsorSelected(seller: Seller) {
    this.sponsorSelected.emit(seller);
  }

  onSponsorTypeChange({ value }) {
    this.sponsorTypeSelected.emit(value);
  }

  private buildForm(sponsor: Sponsor): FormGroup {
    const searchResults = this.store.selectSnapshot(DealerStateSelectors.searchResult);
    const selectedSeller = searchResults ? searchResults.find(s => s.userId === sponsor?.sponsorId) : null;

    return new FormGroup({
      searchValue: new FormControl(selectedSeller ? selectedSeller.fullName : ''),
      sponsorType: new FormControl(sponsor?.sponsorType),
    });
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
